import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BuildingIcon, UserIcon, CheckCircleIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface TransferMoneyProps {
  navigate: (screen: string) => void;
}
export function TransferMoney({
  navigate
}: TransferMoneyProps) {
  const [accountNo, setAccountNo] = useState('');
  const [amount, setAmount] = useState('');
  const [bank, setBank] = useState('');
  const balance = 2450000;
  const numAmount = amount ? parseInt(amount.replace(/,/g, '')) : 0;
  const isResolved = accountNo.length === 10 && bank !== '';
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val) {
      setAmount(parseInt(val).toLocaleString('en-US'));
    } else {
      setAmount('');
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-889">
      <TopBar title="Send Money" showBack onBack={() => navigate('wallet')} data-id="element-890" />

      <div className="px-5 pt-4 pb-6 flex-1 flex flex-col" data-id="element-891">
        {/* Recipient Section */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-892">
          <h3 className="text-sm font-bold text-[#1B3B36] mb-4 flex items-center gap-2" data-id="element-893">
            <UserIcon className="w-4 h-4 text-gray-400" data-id="element-894" /> Recipient Details
          </h3>

          <div className="space-y-4" data-id="element-895">
            <div data-id="element-896">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-897">
                Select Bank
              </label>
              <div className="relative" data-id="element-898">
                <BuildingIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-899" />
                <select value={bank} onChange={e => setBank(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 pl-12 pr-4 text-sm font-bold text-[#1B3B36] focus:outline-none focus:ring-2 focus:ring-[#1B3B36] appearance-none" data-id="element-900">
                  <option value="" disabled data-id="element-901">
                    Choose a bank...
                  </option>
                  <option value="gtb" data-id="element-902">GTBank</option>
                  <option value="access" data-id="element-903">Access Bank</option>
                  <option value="first" data-id="element-904">First Bank</option>
                  <option value="uba" data-id="element-905">UBA</option>
                  <option value="zenith" data-id="element-906">Zenith Bank</option>
                </select>
              </div>
            </div>

            <div data-id="element-907">
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-908">
                Account Number
              </label>
              <input type="text" maxLength={10} value={accountNo} onChange={e => setAccountNo(e.target.value.replace(/\D/g, ''))} placeholder="0000000000" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 px-4 text-sm font-bold text-[#1B3B36] focus:outline-none focus:ring-2 focus:ring-[#1B3B36]" data-id="element-909" />
            </div>

            {isResolved && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-center gap-3" data-id="element-910">
                <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-911" />
                <div data-id="element-912">
                  <p className="text-xs font-bold text-emerald-800" data-id="element-913">John Doe</p>
                  <p className="text-[10px] font-medium text-emerald-600" data-id="element-914">
                    Account verified
                  </p>
                </div>
              </motion.div>}
          </div>
        </div>

        {/* Amount Section */}
        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-915">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-916">
            Amount to Send
          </label>
          <div className="relative mb-2" data-id="element-917">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg" data-id="element-918">
              ₦
            </span>
            <input type="text" value={amount} onChange={handleAmountChange} placeholder="0" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-4 pl-10 pr-4 text-xl font-bold text-[#1B3B36] focus:outline-none focus:ring-2 focus:ring-[#1B3B36]" data-id="element-919" />
          </div>
          <div className="flex justify-between items-center text-xs" data-id="element-920">
            <span className="text-gray-500 font-medium" data-id="element-921">
              Available Balance:
            </span>
            <span className="font-bold text-emerald-600" data-id="element-922">
              {formatNaira(balance)}
            </span>
          </div>
        </div>

        {/* Note Section */}
        <div className="mb-8" data-id="element-923">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1" data-id="element-924">
            Narration (Optional)
          </label>
          <input type="text" placeholder="What's this for?" className="w-full bg-white border border-gray-200 rounded-xl py-3.5 px-4 text-sm font-medium text-[#1B3B36] focus:outline-none focus:ring-2 focus:ring-[#1B3B36] shadow-sm" data-id="element-925" />
        </div>

        <div className="mt-auto" data-id="element-926">
          {/* Summary */}
          {numAmount > 0 && isResolved && <div className="bg-gray-100 rounded-2xl p-4 mb-6" data-id="element-927">
              <div className="flex justify-between text-sm mb-2" data-id="element-928">
                <span className="text-gray-500 font-medium" data-id="element-929">Amount</span>
                <span className="font-bold text-[#1B3B36]" data-id="element-930">
                  {formatNaira(numAmount)}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-3 pb-3 border-b border-gray-200" data-id="element-931">
                <span className="text-gray-500 font-medium" data-id="element-932">Transfer Fee</span>
                <span className="font-bold text-[#1B3B36]" data-id="element-933">₦10</span>
              </div>
              <div className="flex justify-between" data-id="element-934">
                <span className="font-bold text-[#1B3B36]" data-id="element-935">Total Debit</span>
                <span className="font-bold text-lg text-[#1B3B36]" data-id="element-936">
                  {formatNaira(numAmount + 10)}
                </span>
              </div>
            </div>}

          <button disabled={numAmount === 0 || !isResolved} onClick={() => navigate('wallet')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 ${numAmount > 0 && isResolved ? 'bg-[#1B3B36] hover:bg-[#0F2B27] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-937">
            Send Money
          </button>
        </div>
      </div>
    </motion.div>;
}